import { router, useLocalSearchParams, useNavigation } from "expo-router";
import { ScrollView, View } from "react-native";

import { CardComparePrices } from "@/components/browse/CardComparePrices";
import { PublicBrowseSkeleton } from "@/components/browse/PublicBrowseSkeleton";
import { PublicCardSummary } from "@/components/browse/PublicCardSummary";
import { PublicDetailHeader } from "@/components/browse/PublicDetailHeader";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { Screen } from "@/components/ui/Screen";
import { StorefrontWantSlot } from "@/components/wants/StorefrontWantSlot";
import { useMarkInteractive } from "@/hooks/useMarkInteractive";
import { usePublicShopItem } from "@/hooks/usePublicBrowse";
import { shopPath } from "@/lib/shop/publicUrls";

// The storefront's phone content cap, so the page stays centered and narrow on the web.
const MAX_CONTENT_W = 640;

/**
 * `/shop/<slug>/item/<itemId>` — one card on a vendor's shop, opened from the shop's list.
 *
 * The shop's counterpart of a table's card detail (`browse/[eventId]/[itemId]`): the art, what it
 * is, the shop's own price, then "Compare prices" with the same market comps, eBay sold search and
 * recent sales (`CardComparePrices`). It has no "Add to my list": a pick list belongs to a table
 * the customer is standing at, and the shop is a read-only browse (ADR-0056).
 *
 * Like the table's detail it can be a cold launch from a shared link, so Back goes to the whole
 * shop when nothing in the shop is beneath it.
 */
export default function ShopCardScreen() {
  const { slug, itemId } = useLocalSearchParams<{ slug: string; itemId: string; }>();
  const { data, isLoading, error, refetch } = usePublicShopItem(slug ?? null, itemId ?? null);
  useMarkInteractive(!isLoading);
  // Whether the shop is beneath this card in its own stack; a shared link has nothing there.
  const fromShop = (useNavigation().getState()?.index ?? 0) > 0;
  const toShop = () => (fromShop ? router.back() : router.replace(shopPath(slug)));

  if (isLoading) return <PublicBrowseSkeleton layout="detail" />;

  if (error || !data) {
    return (
      <Screen role="main" accessibilityLiveRegion="assertive">
        <EmptyState
          mark
          title="Couldn't load this card"
          subtitle={error?.message ?? "Couldn't load this card."}
          action={
            <View className="items-center gap-2">
              <Button title="Try again" variant="outline" onPress={() => refetch()} />
              <Button
                title="See the whole shop"
                variant="ghost"
                onPress={() => router.replace(shopPath(slug))}
              />
            </View>
          }
        />
      </Screen>
    );
  }

  return (
    <Screen testID="shop-card-screen">
      <View className="w-full flex-1 self-center" role="main" style={{ maxWidth: MAX_CONTENT_W }}>
        <PublicDetailHeader
          title={data.item.product_name}
          cold={fromShop
            ? undefined
            : { label: "Whole shop", accessibilityLabel: "See the whole shop" }}
          onBack={toShop}
          testID="shop-card-back"
        />

        <ScrollView className="flex-1" contentContainerClassName="gap-3 px-4 pb-10 pt-3">
          <PublicCardSummary detail={data} testIDPrefix="shop-card" />
          <StorefrontWantSlot slug={slug} itemId={itemId} cardName={data.item.product_name} />
          <CardComparePrices detail={data} />
        </ScrollView>
      </View>
    </Screen>
  );
}
